import { z } from 'zod';
import { UserSnapshotSchema } from './user';

export const CONSENT_FORM_STATUS = {
  CREATED: 'created',
  TEMPLATE: 'template',
  SENT: 'sent',
  COMPLETED: 'completed',
  SIGNED: 'signed',
  DECLINED: 'declined',
  NEW: 'new',
} as const;

export const ConsentTemplateSchema = z.object({
  id: z.string(),
  externalId: z.string(),
  name: z.string(),
  version: z.string(),
});

export const ConsentSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  status: z.nativeEnum(CONSENT_FORM_STATUS),
  signedAt: z.string().datetime().optional(),
  template: ConsentTemplateSchema.nullable(),
  confirmedBy: UserSnapshotSchema.nullable(),
  url: z.string().url().nullish(),
});

export const ConsentResponseSchema = z.object({
  sent: z.array(ConsentSchema),
  pending: ConsentTemplateSchema.optional(),
});

export const ConsentFormTemplateItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  version: z.string(),
  protocol: z.object({
    id: z.string().uuid(),
    name: z.string(),
  }),
  isActive: z.boolean(),
});

// Set active template schemas
export const SetActiveTemplateParametersSchema = z.object({
  templateId: z.string().uuid('Invalid template ID format'),
});

export const SetActiveTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  isActive: z.boolean(),
});

export const InitiateFileUploadResponseSchema = z.object({
  url: z.string(),
  resourceKey: z.string(),
  headers: z.record(z.string()),
});

// Consent request schemas
export const ConfirmConsentRequestParamsSchema = z.object({
  subjectId: z.string(),
  templateId: z.string(),
  signedAt: z.date(),
});

export const ConsentRequestParamsSchema = z.object({
  subjectId: z.string(),
  templateId: z.string(),
});

// Field coordinates schema
export const FieldCoordinatesSchema = z.object({
  pageNumber: z.number(),
  xPosition: z.string(),
  yPosition: z.string(),
  width: z.string(),
  height: z.string(),
});

// Create template schemas
export const CreateTemplateParamsSchema = z.object({
  name: z.string(),
  protocolId: z.string(),
  version: z.string(),
  resourceKey: z.string(),
  originalName: z.string(),
  signatureField: FieldCoordinatesSchema,
  nameField: FieldCoordinatesSchema,
  dateField: FieldCoordinatesSchema,
});

export const InitiateFileUploadParamsSchema = z.object({
  file: z.instanceof(File),
});

export const InitiateFileUploadResultSchema = z.object({
  resourceKey: z.string(),
  originalName: z.string(),
});

export const GetConsentFormTemplatesResponseSchema = z.object({
  items: z.array(ConsentFormTemplateItemSchema),
});

export const SingleConsentFormTemplateResponseSchema = ConsentFormTemplateItemSchema.extend({
  metadata: z.object({
    signatureField: FieldCoordinatesSchema,
    nameField: FieldCoordinatesSchema,
    dateField: FieldCoordinatesSchema,
  }),
  file: z.object({
    id: z.string().uuid(),
    name: z.string(),
    url: z.string(),
  }),
});

export type ConsentFormStatus = (typeof CONSENT_FORM_STATUS)[keyof typeof CONSENT_FORM_STATUS] | undefined;
export type ConsentTemplate = z.infer<typeof ConsentTemplateSchema>;
export type Consent = z.infer<typeof ConsentSchema>;
export type ConsentResponse = z.infer<typeof ConsentResponseSchema>;
export type ConsentFormTemplateItem = z.infer<typeof ConsentFormTemplateItemSchema>;
export type GetConsentFormTemplatesResponse = z.infer<typeof GetConsentFormTemplatesResponseSchema>;
export type SingleConsentFormTemplateResponse = z.infer<typeof SingleConsentFormTemplateResponseSchema>;
export type SetActiveTemplateParameters = z.infer<typeof SetActiveTemplateParametersSchema>;
export type SetActiveTemplateResponse = z.infer<typeof SetActiveTemplateResponseSchema>;
export type InitiateFileUploadResponse = z.infer<typeof InitiateFileUploadResponseSchema>;
export type ConfirmConsentRequestParams = z.infer<typeof ConfirmConsentRequestParamsSchema>;
export type ConsentRequestParams = z.infer<typeof ConsentRequestParamsSchema>;
export type FieldCoordinates = z.infer<typeof FieldCoordinatesSchema>;
export type CreateTemplateParams = z.infer<typeof CreateTemplateParamsSchema>;
export type InitiateFileUploadParams = z.infer<typeof InitiateFileUploadParamsSchema>;
export type InitiateFileUploadResult = z.infer<typeof InitiateFileUploadResultSchema>;
